import { Globe2, Handshake, Route, ShieldCheck, ArrowRight } from "lucide-react";
import MeetTheTeam from "@/components/MeetTheTeam";

const HERO = "https://images.unsplash.com/photo-1765153743373-488f6107948d?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

const PILLARS = [
  { icon: Globe2, title: "Explore the world", text: "Discover residency and citizenship opportunities across 35+ countries on one interactive globe." },
  { icon: Route, title: "Build your pathway", text: "Answer a few questions and we assemble a tailored, step-by-step journey around your objective." },
  { icon: Handshake, title: "One coordinated team", text: "A single dedicated adviser coordinates every specialist so you never juggle providers." },
  { icon: ShieldCheck, title: "Clarity you can trust", text: "Transparent, indicative information at every step — with professional review where it matters." },
];

const STEPS = [
  "Explore destinations and opportunities on the globe",
  "Open a country and review the five key facts",
  "Choose a pathway and define your objective",
  "We build your dynamic, tailored journey",
  "Your adviser coordinates every service to completion",
];

export default function AboutPage({ onContact }) {
  return (
    <div className="mx-auto max-w-lg pb-8" data-testid="about-page">
      <div className="relative h-52 w-full overflow-hidden">
        <img src={HERO} alt="Global cities" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07090E] via-[#07090E]/40 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400/90">About Us</p>
          <h1 className="mt-1 text-2xl font-semibold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
            One objective. One relationship. One journey.
          </h1>
        </div>
      </div>

      <div className="px-4">
        <p className="mt-5 text-sm leading-relaxed text-slate-300">
          Migration Portal is a global residency and investment platform built around a simple idea: your international
          move should feel like one coordinated journey, not a scramble across dozens of unconnected providers.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-slate-300">
          We bring the world's leading migration, investment, property, tax and relocation services together under a
          single dedicated adviser — so you always have one point of contact from first exploration to final settlement.
        </p>

        <div className="mt-6 grid grid-cols-1 gap-3">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="flex gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.03] p-4">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-300/10 text-amber-300">
                  <Icon size={18} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white">{p.title}</h3>
                  <p className="mt-0.5 text-[13px] leading-snug text-slate-300">{p.text}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-7">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400/90">How it works</p>
          <div className="space-y-2.5">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-amber-300/40 font-mono text-xs text-amber-200">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[13px] text-slate-200">{s}</span>
              </div>
            ))}
          </div>
        </div>

        <MeetTheTeam />

        <div className="mt-7 rounded-2xl border p-5 text-center" style={{ borderColor: "rgba(226,194,117,0.3)", background: "linear-gradient(135deg, rgba(226,194,117,0.12), rgba(13,17,26,0.4))" }}>
          <h3 className="text-lg font-semibold text-white">Ready to explore your future?</h3>
          <p className="mx-auto mt-1 max-w-xs text-sm text-slate-300">
            Speak with a dedicated adviser about the opportunities that fit your goals.
          </p>
          <button
            data-testid="about-contact-btn"
            onClick={onContact}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-300 px-6 py-2.5 text-sm font-semibold text-[#07090E]"
          >
            Talk to an adviser <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
